import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../context/AuthContext';
import * as authAPI from '../services/operations/authAPI';
import { useGoogleLogin } from '../hooks/useGoogleLogin';
import SEO from '../components/SEO';

export default function LoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [, setIsGoogleAccount] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { initializeGoogleLogin, cleanup, directGoogleLogin } = useGoogleLogin();

  const handleGoogleClick = async () => {
    // console.log('Google button clicked');

    if (googleLoading) {
      // console.log('Already loading, ignoring click');
      return; // Prevent multiple clicks
    }

    setGoogleLoading(true);
    try {
      // Check if Google services are available
      if (window.google && window.google.accounts) {
        // console.log('Google services available, calling directGoogleLogin');
        directGoogleLogin();
      } else {
        console.error('Google services not available:', {
          google: !!window.google,
          accounts: !!(window.google && window.google.accounts)
        });
        toast.error('Google services not available. Please refresh the page.');
      }
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Google login failed. Please try again.');
    } finally {
      // Reset loading state after a short delay
      setTimeout(() => {
        // console.log('Resetting loading state');
        setGoogleLoading(false);
      }, 3000);
    }
  };

  // Initialize Google login when component mounts
  useEffect(() => {
    // Wait for Google script to load
    const checkGoogleLoaded = () => {
      if (window.google) {
        // console.log('Google script loaded, initializing...');
        initializeGoogleLogin();
      } else {
        // console.log('Waiting for Google script to load...');
        setTimeout(checkGoogleLoaded, 100);
      }
    };

    checkGoogleLoaded();

    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, [initializeGoogleLogin, cleanup]);


  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = 'Email required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    // Always require password
    if (!formData.password) newErrors.password = 'Password required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkEmailAccountType = async (email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    try {
      const res = await (await import('../services/operations/authAPI')).checkAccountType(email);
      if (res && res.success && res.exists) {
        setIsGoogleAccount(!!res.google);
      } else {
        setIsGoogleAccount(false);
      }
    } catch (err) {
      console.error('Account type check failed', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (name === 'email') {
      // clear google flag while editing
      setIsGoogleAccount(false);
    }
  };

  const handleEmailBlur = () => {
    checkEmailAccountType(formData.email);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Allow both Google and password login - users can use either method

    if (validateForm()) {
      (async () => {
        setLoading(true);
        try {
          const res = await authAPI.login({ email: formData.email, password: formData.password });
          if (res && res.success) {
            toast.success(res.message || 'Login successful');
            login(res.user || res.data?.user);
            navigate('/profile');
          } else if (res && res.incompleteProfile) {
            // Profile incomplete - redirect to signup to complete profile
            toast.info(res.message || 'Please complete your profile');
            navigate('/SignUp', {
              state: {
                googleUser: res.user,
                googleSignup: true,
                missingFields: [],
                tempToken: res.tempToken
              }
            });
          } else {
            toast.error(res.message || 'Login failed');
          }
        } catch (err) {
          // Check if error response has incompleteProfile flag
          if (err?.response?.data?.incompleteProfile) {
            toast.info(err.response.data.message || 'Please complete your profile');
            navigate('/SignUp', {
              state: {
                googleUser: err.response.data.user,
                googleSignup: true,
                missingFields: [],
                tempToken: err.response.data.tempToken
              }
            });
          } else {
            const msg = err?.response?.data?.message || err?.message || 'Login failed. Please try again.';
            toast.error(msg);
          }
        } finally {
          setLoading(false);
        }
      })();
    }
  };


  return (
    <>
      <SEO
        title="Login - ReferMe | Access Your Study Materials"
        description="Login to ReferMe to access your purchased study notes, previous year papers, and engineering exam materials. Continue your learning journey."
        url="/login"
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white flex items-center justify-center p-6">
        <div className="w-full max-w-md">

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-gradient-to-r from-white via-gray-100 to-gray-300 text-transparent bg-clip-text">
              Welcome Back
            </h1>
            <p className="text-gray-400 mt-3 text-base">
              Sign in to continue your learning journey
            </p>
          </div>

          {/* Main Card */}
          <div className="relative overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-xl p-6 shadow-2xl">

            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none" />

            <div className="relative z-10 space-y-5">

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 text-indigo-400" size={18} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => { setFocusedField(null); handleEmailBlur(); }}
                    placeholder="example@gmail.com"
                    className={`w-full bg-gray-800/50 border rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none transition hover:bg-gray-800/70 ${errors.email
                        ? "border-red-500"
                        : focusedField === "email"
                          ? "border-indigo-400 ring-2 ring-indigo-400/20"
                          : "border-gray-700"
                      }`}
                  />
                </div>
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Password</label>

                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 text-purple-400" size={18} />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("password")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Enter your password"
                    className={`w-full bg-gray-800/50 border rounded-lg pl-10 pr-10 py-2.5 text-sm focus:outline-none transition hover:bg-gray-800/70 ${errors.password
                        ? "border-red-500"
                        : focusedField === "password"
                          ? "border-purple-400 ring-2 ring-purple-400/20"
                          : "border-gray-700"
                      }`}
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-200 transition"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Forgot Password */}
              <div className="text-right">
                <button
                  onClick={() => navigate("/forgot-password")}
                  className="text-indigo-400 hover:text-indigo-300 text-xs font-medium transition"
                >
                  Forgot Password?
                </button>
              </div>

              {/* Login Button */}
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`w-full py-2.5 rounded-lg text-white font-semibold text-sm flex items-center justify-center gap-2
              bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600
              transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-indigo-500/30 active:scale-95
              ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <Sparkles size={16} />
                {loading ? "Signing in..." : "Sign In"}
                <ArrowRight size={16} />
              </button>

              {/* Divider */}
              <div className="flex items-center gap-4 py-1">
                <hr className="flex-1 border-gray-700/50" />
                <span className="text-gray-400 text-xs font-medium">OR</span>
                <hr className="flex-1 border-gray-700/50" />
              </div>

              {/* Google Sign-In */}
              <button
                onClick={handleGoogleClick}
                disabled={googleLoading}
                className={`w-full py-2.5 rounded-lg bg-gray-800/50 border border-gray-700 hover:bg-gray-800/80 hover:border-gray-600
              transition-all duration-300 flex items-center justify-center gap-2.5 ${googleLoading ? "opacity-60 cursor-not-allowed" : ""
                  }`}
              >
                {googleLoading ? (
                  <div className="w-4 h-4 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
                ) : (
                  <>
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google login icon"
                      className="h-4 w-4"
                    />
                    <span className="text-white text-sm font-medium">
                      Continue with Google
                    </span>
                  </>
                )}
              </button>

              {/* Bottom Link */}
              <p className="text-center text-gray-400 text-sm mt-2">
                Don't have an account?{" "}
                <button
                  onClick={() => navigate("/SignUp")}
                  className="text-indigo-400 font-semibold hover:text-indigo-300 transition"
                >
                  Create Account
                </button>
              </p>

            </div>
          </div>
        </div>
      </div>
    </>
  );

}